import { dehydrate } from '@tanstack/react-query';
import { persistQueryClientSave } from '@tanstack/react-query-persist-client';

let activeConfig = null;

/** Register query client + persister used for imperative foreground/background writes. */
export function registerQueryClientPersist(config) {
  activeConfig = config;
  return () => {
    if (activeConfig === config) {
      activeConfig = null;
    }
  };
}

function buildPersistedClient(config) {
  const { queryClient, buster = '', dehydrateOptions } = config;
  return {
    buster,
    timestamp: Date.now(),
    clientState: dehydrate(queryClient, dehydrateOptions),
  };
}

/**
 * Dehydrate the live cache and write immediately.
 * Sync mirror (localStorage) runs first; native Preferences write follows async.
 */
export async function persistQueryClientNow(reason = 'manual') {
  if (!activeConfig) return false;

  const persistedClient = buildPersistedClient(activeConfig);
  const { persister } = activeConfig;

  if (typeof persister.writeSync === 'function') {
    persister.writeSync(persistedClient, reason);
  }

  try {
    await persistQueryClientSave({
      queryClient: activeConfig.queryClient,
      persister: activeConfig.persister,
      buster: activeConfig.buster,
      dehydrateOptions: activeConfig.dehydrateOptions,
    });
    return true;
  } catch (err) {
    console.warn(`[query] persistQueryClientNow failed (${reason}):`, err);
    return false;
  }
}

/** Synchronous persist for app background — must complete before process suspension. */
export function persistQueryClientSyncNow(reason = 'background') {
  if (!activeConfig) return false;

  try {
    const persistedClient = buildPersistedClient(activeConfig);
    const { persister } = activeConfig;
    if (typeof persister.writeSync === 'function') {
      persister.writeSync(persistedClient, reason);
      return true;
    }
    return false;
  } catch (err) {
    console.warn(`[query] persistQueryClientSyncNow failed (${reason}):`, err);
    return false;
  }
}

/** Read persisted JSON from sync mirror — for verification / restore fallback. */
export function readPersistedClientSyncMirror(storageKey) {
  if (typeof window === 'undefined' || !storageKey) return null;
  try {
    return window.localStorage.getItem(storageKey);
  } catch {
    return null;
  }
}
